import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import { CButton, CSpinner } from "@coreui/react";
import Sidebar from "../../../components/SideBar";
import Navbar from "../../../components/NavBar";
import LogoutModal from "../../../components/logout-moal/LogoutModal";
import { getPendingProfileChangeRequests } from "../../../../service-layer/ProfileChangeRequestService";
import { useStateContext } from "../../../../contextProvider";
import AuthGuard from "../../../components/AuthGuard/AuthGuard";


const PendingProfileChanges = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { adminPage, setAdminPage } = useStateContext();

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);

  useEffect(() => {
    if (window.localStorage.getItem("auth_token") === null) navigate("/login");
  }, [navigate]);

  useEffect(() => {
    loadPendingRequests();
  }, []);

  const loadPendingRequests = async () => {
    try {
      setLoading(true);
      const response = await getPendingProfileChangeRequests();
      if (response && response.status === 200) {
        setRequests(response.data);
      }
    } catch (error) {
      console.error("Error loading pending requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const ReviewButton = ({ data }) => {
    const handleReviewClick = () => {
      // Navigate to the review page with the token
      // This will now require authentication
      navigate(`/profile-change-review?token=${data.reviewToken}`);
    };
    return (
      <CButton
        color="primary"
        size="sm"
        onClick={handleReviewClick}
      >
        {t("review_request")}
      </CButton>
    );
  };

  const columns = [
    {
      field: "doctorName",
      headerName: t("doctor_name"),
      filter: true,
      floatingFilter: true,
      valueGetter: (params) => `${params.data.doctorName} ${params.data.doctorSurname}`,
    },
    {
      field: "doctorEmail",
      headerName: t("current_email"),
      filter: true,
      floatingFilter: true,
    },
    {
      field: "requestDate",
      headerName: t("request_date"),
      filter: true,
      floatingFilter: true,
      valueFormatter: (params) => new Date(params.value).toLocaleString(),
    },
    {
      field: "changes",
      headerName: t("changes_summary"),
      flex: 2,
      valueGetter: (params) => {
        const changes = [];
        if (params.data.currentEmail !== params.data.newEmail) {
          changes.push(`${t("email")}: ${params.data.newEmail}`);
        }
        if (params.data.currentSpecialization !== params.data.newSpecialization) {
          changes.push(`${t("specialization")}: ${params.data.newSpecialization}`);
        }
        if (params.data.currentOperationalUnit !== params.data.newOperationalUnit) {
          changes.push(`${t("operationalUnit")}: ${params.data.newOperationalUnit || '-'}`);
        }
        return changes.join(' | ');
      },
    },
    {
      field: "review",
      headerName: t("actions"),
      sortable: false,
      filter: false,
      cellRenderer: ReviewButton,
      width: 150,
    },
  ];

  const defaultColDef = {
    flex: 1,
    resizable: true,
  };

  const handleConfirm = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("name");
    localStorage.removeItem("surname");
    localStorage.removeItem("authority");
    setShowLogoutModal(false);
    navigate("/");
  };

  const token = window.localStorage.getItem("auth_token");

  return (
    <AuthGuard requireAdmin={true}>
      {token && (
        <div className="d-flex">
          <div>
            <Sidebar />
          </div>
          <div
            style={{
              flex: "1 1 auto",
              display: "flex",
              flexFlow: "column",
              height: "100vh",
              overflowY: "hidden",
            }}
          >
            <Navbar />
            <div style={{ height: "100%" }}>
              <div
                style={{
                  padding: "20px",
                  height: "calc(100% - 64px)",
                  overflowY: "auto",
                }}
              >
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h2>{t("pending_profile_change_requests")}</h2>
                  <CButton
                    color="primary"
                    onClick={loadPendingRequests}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <CSpinner size="sm" /> {t("loading")}
                      </>
                    ) : (
                      t("refresh")
                    )}
                  </CButton>
                </div>

                {loading ? (
                  <div className="text-center py-5">
                    <CSpinner color="primary" size="lg" />
                  </div>
                ) : (
                  <div className="ag-theme-alpine" style={{ height: "600px", width: "100%" }}>
                    <AgGridReact
                      rowData={requests}
                      columnDefs={columns}
                      defaultColDef={defaultColDef}
                      domLayout="normal"
                      pagination={true}
                      paginationPageSize={20}
                      suppressCellFocus={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!token && <div className="login-container"></div>}

      <LogoutModal
        show={showLogoutModal}
        handleConfirm={handleConfirm}
        handleClose={() => setShowLogoutModal(false)}
        title={t("login")}
        message={t("session_expired_please_login_again")}
        tag={"Login"}
      />
    </AuthGuard>
  );
};

export default PendingProfileChanges;